import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Text,
  Input,
  Button,
  Alert,
  Spinner,
  Label,
} from '@nimbus-ds/components';
import { n8nConfigApi, N8nConfig, storeApi, StoreCredentials } from '../services/api';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<N8nConfig>({ host: '' });
  const [credentials, setCredentials] = useState<StoreCredentials | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'danger';
    text: string;
  } | null>(null);

  useEffect(() => {
    Promise.all([
      n8nConfigApi.get().then((res) => setConfig(res.data || { host: '' })),
      storeApi.getCredentials().then((res) => setCredentials(res.data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyToClipboard = (text: string, field: string) => {
    // Fallback method that works in iframes
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // If copy fails, show the value in a prompt for manual copy
      window.prompt('Copia este valor:', text);
    }
    document.body.removeChild(textArea);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await n8nConfigApi.save(config);
      setMessage({
        type: 'success',
        text: 'Configuracion guardada correctamente',
      });
    } catch {
      setMessage({ type: 'danger', text: 'Error al guardar la configuracion' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const res = await n8nConfigApi.test();
      if (res.data.success) {
        setMessage({
          type: 'success',
          text: `Conexion exitosa (${res.data.latency}ms)`,
        });
      } else {
        setMessage({
          type: 'danger',
          text: res.data.error || 'Error de conexion',
        });
      }
    } catch {
      setMessage({ type: 'danger', text: 'Error al probar la conexion' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box padding="4" display="flex" justifyContent="center">
        <Spinner size="large" />
      </Box>
    );
  }

  return (
    <Box padding="4">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="base" fontWeight="bold">
          Configuracion de n8n
        </Text>
        <Box display="flex" gap="2">
          <Button appearance="transparent" onClick={() => navigate('/webhooks')}>
            Webhooks
          </Button>
          <Button onClick={() => navigate('/')}>
            Ir a n8n
          </Button>
        </Box>
      </Box>

      {message && (
        <Box marginTop="4">
          <Alert
            appearance={message.type}
            title={message.type === 'success' ? 'Exito' : 'Error'}
          >
            {message.text}
          </Alert>
        </Box>
      )}

      <Box marginTop="4">
        <Card>
          <Card.Body>
            <Box display="flex" flexDirection="column" gap="4">
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="host">Host de n8n</Label>
                <Input
                  id="host"
                  placeholder="https://tu-dominio.com/n8n"
                  value={config.host}
                  onChange={(e) => setConfig({ ...config, host: e.target.value })}
                />
              </Box>
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="username">Usuario (opcional)</Label>
                <Input
                  id="username"
                  placeholder="admin"
                  value={config.username || ''}
                  onChange={(e) =>
                    setConfig({ ...config, username: e.target.value })
                  }
                />
              </Box>
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="password">Contrasena (opcional)</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="********"
                  value={config.password || ''}
                  onChange={(e) =>
                    setConfig({ ...config, password: e.target.value })
                  }
                />
              </Box>
            </Box>
          </Card.Body>
          <Card.Footer>
            <Box display="flex" gap="2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
              <Button
                appearance="transparent"
                onClick={handleTest}
                disabled={testing || !config.host}
              >
                {testing ? 'Probando...' : 'Probar conexion'}
              </Button>
            </Box>
          </Card.Footer>
        </Card>
      </Box>

      {credentials && (
        <Box marginTop="4">
          <Text fontSize="base" fontWeight="bold">
            Credenciales de TiendaNube
          </Text>
          <Box marginTop="2">
            <Card>
              <Card.Body>
                <Box display="flex" flexDirection="column" gap="4">
                  <Box display="flex" flexDirection="column" gap="1">
                    <Label>Store ID</Label>
                    <Box display="flex" gap="2" alignItems="center">
                      <Input
                        value={credentials.storeId}
                        readOnly
                      />
                      <Button
                        appearance="transparent"
                        onClick={() => copyToClipboard(credentials.storeId, 'storeId')}
                      >
                        {copied === 'storeId' ? 'Copiado!' : 'Copiar'}
                      </Button>
                    </Box>
                  </Box>
                  <Box display="flex" flexDirection="column" gap="1">
                    <Label>Access Token</Label>
                    <Box display="flex" gap="2" alignItems="center">
                      <Input
                        value={credentials.accessToken}
                        readOnly
                        type="password"
                      />
                      <Button
                        appearance="transparent"
                        onClick={() => copyToClipboard(credentials.accessToken, 'accessToken')}
                      >
                        {copied === 'accessToken' ? 'Copiado!' : 'Copiar'}
                      </Button>
                    </Box>
                  </Box>
                  {credentials.storeName && (
                    <Box display="flex" flexDirection="column" gap="1">
                      <Label>Nombre de la tienda</Label>
                      <Input value={credentials.storeName} readOnly />
                    </Box>
                  )}
                </Box>
              </Card.Body>
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Settings;
